/**
 * Échantillonne N points le long d'une orbite képlérienne (a, e, i, Ω, ω).
 * Retourne des positions héliocentriques en référentiel écliptique J2000 (UA).
 *
 * Pour le tracé visuel d'une orbite complète, on échantillonne uniformément
 * en anomalie excentrique E (et non en M ou ν), ce qui donne un tracé visuellement
 * équilibré sans qu'il faille résoudre Kepler à chaque point.
 */

export type Vec3 = readonly [number, number, number];

export interface OrbitElements {
  a_au: number;
  e: number;
  i_deg: number;
  Omega_deg: number;
  omega_deg: number;
}

const DEG = Math.PI / 180;

/** Date julienne de l'époque J2000 (2000-01-01 12:00 UT). */
export const JD_J2000 = 2451545.0;

/** Résout l'équation de Kepler M = E - e·sin(E) par Newton-Raphson. */
export function solveKepler(M_rad: number, e: number, tol = 1e-10, maxIter = 30): number {
  let E = e < 0.5 ? M_rad : Math.PI;
  for (let i = 0; i < maxIter; i++) {
    const dE = (E - e * Math.sin(E) - M_rad) / (1 - e * Math.cos(E));
    E -= dE;
    if (Math.abs(dE) < tol) return E;
  }
  return E;
}

/**
 * Position cartésienne héliocentrique écliptique J2000 à anomalie moyenne M.
 * Utilise la même convention de rotations Rz(Ω)·Rx(i)·Rz(ω) que la backend.
 */
export function cartesianAt(el: OrbitElements, M_deg: number): Vec3 {
  if (el.e >= 1) return [0, 0, 0]; // hyperboliques non supportées
  const M = M_deg * DEG;
  const E = solveKepler(M, el.e);
  const cosE = Math.cos(E);
  const sinE = Math.sin(E);
  const xp = el.a_au * (cosE - el.e);
  const yp = el.a_au * Math.sqrt(1 - el.e * el.e) * sinE;

  const cosw = Math.cos(el.omega_deg * DEG);
  const sinw = Math.sin(el.omega_deg * DEG);
  const cosi = Math.cos(el.i_deg * DEG);
  const sini = Math.sin(el.i_deg * DEG);
  const cosO = Math.cos(el.Omega_deg * DEG);
  const sinO = Math.sin(el.Omega_deg * DEG);

  const x = (cosO * cosw - sinO * sinw * cosi) * xp +
            (-cosO * sinw - sinO * cosw * cosi) * yp;
  const y = (sinO * cosw + cosO * sinw * cosi) * xp +
            (-sinO * sinw + cosO * cosw * cosi) * yp;
  const z = (sinw * sini) * xp + (cosw * sini) * yp;
  return [x, y, z];
}

/** Vitesse moyenne (degrés / jour) d'un corps héliocentrique selon Kepler 3.
 *  T (jours) = 365.25 · a^(3/2)   (a en UA) → n = 360 / T */
export function meanMotionDegPerDay(a_au: number): number {
  const T_days = 365.25 * Math.pow(a_au, 1.5);
  return 360 / T_days;
}

/** M(t) = M₀ + n·(t − JD_J2000), modulo 360. */
export function evolvedM(M0_deg: number, a_au: number, tJd: number): number {
  const M = M0_deg + meanMotionDegPerDay(a_au) * (tJd - JD_J2000);
  return ((M % 360) + 360) % 360;
}

/**
 * Sample N+1 points (closed loop, last = first) along the orbit.
 * Defaults to 96 segments, plenty smooth at typical view scale.
 */
export function sampleOrbit(el: OrbitElements, n = 96): Vec3[] {
  const { a_au, e, i_deg, Omega_deg, omega_deg } = el;
  if (e >= 1) return []; // ne supporte que les orbites elliptiques

  const cosw = Math.cos(omega_deg * DEG);
  const sinw = Math.sin(omega_deg * DEG);
  const cosi = Math.cos(i_deg * DEG);
  const sini = Math.sin(i_deg * DEG);
  const cosO = Math.cos(Omega_deg * DEG);
  const sinO = Math.sin(Omega_deg * DEG);

  const sqrt1me2 = Math.sqrt(1 - e * e);
  const out: Vec3[] = [];
  for (let k = 0; k <= n; k++) {
    const E = (k / n) * 2 * Math.PI;
    const cosE = Math.cos(E);
    const sinE = Math.sin(E);
    // Position dans le plan orbital (origine au foyer = Soleil)
    const xp = a_au * (cosE - e);
    const yp = a_au * sqrt1me2 * sinE;

    // Rotations Rz(Ω)·Rx(i)·Rz(ω) appliquées à (xp, yp, 0)
    const x = (cosO * cosw - sinO * sinw * cosi) * xp +
              (-cosO * sinw - sinO * cosw * cosi) * yp;
    const y = (sinO * cosw + cosO * sinw * cosi) * xp +
              (-sinO * sinw + cosO * cosw * cosi) * yp;
    const z = (sinw * sini) * xp + (cosw * sini) * yp;
    out.push([x, y, z]);
  }
  return out;
}
